import { Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Actions, ofActionDispatched, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { combineLatest } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { ConnectionInfo } from '../../../generated/signalR/connection-info';
import { GitVersionInfo } from '../../../generated/signalR/git-version-info';
import { WebRtcService } from '../../../generated/swagger';
import { NotificationType } from '../../samwebDescription/samweb/notification/notificationType';
import { SignalrService } from '../../services/realtime/signalr.service';
import { SessionCookieService } from '../../services/session-cookie.service';
import { WebRtcConnectionService } from '../../services/webRtc/web-rtc-connection.service';
import { ToastUserInfoNotification } from '../notification/notification-toastr/notification-toastr.actions';
import { ClientPhoneState } from '../samwin/client-phone/client-phone.state';
import { SessionTypeEnum } from '../session/session-type-enum';
import { LongRunningContextState } from './running-actions/long-running-context.state';
import { Sccs1ConnectionState } from './sccs1-connection/sccs1-connection.state';
import { RealTimeConnectionStatus } from './server-communication-status';
import {
   ConnectSignalR,
   DisconnectSignalR,
   FinishHttpLoading,
   RealTimeConnectionStatusChanged,
   RealTimeConnectorStatusChanged,
   ReconnectSignalR,
   SamwinLogonErrorOccurred,
   StartHttpLoading,
   UserSystemMonitorDataChanged,
   WebRtcCloseConnection,
   WebRtcIceCandidatesAddedByBrowser,
   WebRtcIceCandidatesAddedByServer,
   WebRtcNegotiationNeeded,
   WebRtcSdpSignalingAdded,
   WebRtcStartClientEndpoint,
} from './server-communication.actions';

export interface IUserSessionMonitoringInfo {
   readonly sessionId: string;
   readonly sessionType: SessionTypeEnum;
   readonly availableRealtimeConnections: number;
   readonly isLongTermSession: boolean;
}

export interface ServerCommunicationStateModel {
   isLoading: boolean;
   sessionIdUsedForSignalR: string;
   connectionInfo: ConnectionInfo;
   realTimeConnectionStatus: RealTimeConnectionStatus;
   isRegistrationConfirmedAtServer: boolean;
   responsibleHostForUser: string;
   sessionInfos: { [sessionId: string]: IUserSessionMonitoringInfo };
   userVersionInfo: GitVersionInfo;
}

@State<ServerCommunicationStateModel>({
   name: 'serverCommunication',
   defaults: {
      isLoading: false,
      realTimeConnectionStatus: RealTimeConnectionStatus.DISCONNECTED,
      sessionIdUsedForSignalR: null,
      connectionInfo: null,
      isRegistrationConfirmedAtServer: false,
      responsibleHostForUser: null,
      sessionInfos: {},
      userVersionInfo: null,
   },
   children: [Sccs1ConnectionState, LongRunningContextState],
})
@Injectable()
export class ServerCommunicationState {
   public get webRtcConnectionService(): WebRtcConnectionService {
      return this.injector.get(WebRtcConnectionService);
   }

   constructor(
      private signalRService: SignalrService,
      private store: Store,
      private injector: Injector,
      private webRtcService: WebRtcService,
      private translateService: TranslateService,
      actions$: Actions,
      sessionCookieService: SessionCookieService
   ) {
      sessionCookieService.sessionCookieValue$
         .pipe(
            map((sessionValue) => {
               const sessionIdUsedForSignalR = this.store.selectSnapshot(ServerCommunicationState.getSessionIdUsedForSignalR);
               const connectionState = this.store.selectSnapshot(ServerCommunicationState.getRealtimeConnectionState);

               if (sessionIdUsedForSignalR !== sessionValue && connectionState === RealTimeConnectionStatus.CONNECTED) {
                  console.log('Cookie mismatch between SignalR and Browser detected! Stop SignalR.');
                  this.store.dispatch(DisconnectSignalR);
               }
            })
         )
         .subscribe();

      actions$
         .pipe(
            ofActionDispatched(ReconnectSignalR),
            debounceTime(500),
            tap((reconnectAction) => this.store.dispatch(new ConnectSignalR()))
         )
         .subscribe();
   }

   @Selector([ServerCommunicationState])
   public static getIsHttpLoading(state: ServerCommunicationStateModel) {
      return state.isLoading;
   }

   @Selector([ServerCommunicationState])
   public static getRealtimeConnectionState(state: ServerCommunicationStateModel): RealTimeConnectionStatus {
      return state.realTimeConnectionStatus;
   }

   @Selector([ServerCommunicationState])
   public static getUserVersionInfo(state: ServerCommunicationStateModel): GitVersionInfo {
      return state.userVersionInfo;
   }

   @Selector([ServerCommunicationState])
   public static getIsRegistrationConfirmedAtServer(state: ServerCommunicationStateModel) {
      return state.isRegistrationConfirmedAtServer;
   }

   @Selector([ServerCommunicationState])
   public static getSessionIdUsedForSignalR(state: ServerCommunicationStateModel) {
      return state.sessionIdUsedForSignalR;
   }

   @Selector([ServerCommunicationState])
   public static getSessionInfos(state: ServerCommunicationStateModel): IUserSessionMonitoringInfo[] {
      return _.values(state.sessionInfos);
   }

   @Selector([ServerCommunicationState])
   public static getSignalRConnectionId(state: ServerCommunicationStateModel) {
      return state.connectionInfo?.connectionId;
   }

   @Selector([ServerCommunicationState])
   public static getSignalRConnectionServerVersionInfo(state: ServerCommunicationStateModel): GitVersionInfo {
      return state.connectionInfo?.versionInfo;
   }
   @Selector([ServerCommunicationState])
   public static getWebApiServerAddress(state: ServerCommunicationStateModel): string {
      return state.connectionInfo?.webApiServerAddress;
   }

   @Selector([ServerCommunicationState])
   public static getResponsibleHostForUser(state: ServerCommunicationStateModel) {
      return state.responsibleHostForUser;
   }

   @Action(StartHttpLoading)
   public startHttpLoading(ctx: StateContext<ServerCommunicationStateModel>, action: StartHttpLoading) {
      ctx.patchState({
         isLoading: true,
      });
   }

   @Action(FinishHttpLoading)
   public finishHttpLoading(ctx: StateContext<ServerCommunicationStateModel>, action: FinishHttpLoading) {
      ctx.patchState({
         isLoading: false,
      });
   }

   @Action(RealTimeConnectorStatusChanged)
   public realTimeConnectorStatusChanged(ctx: StateContext<ServerCommunicationStateModel>, action: RealTimeConnectorStatusChanged) {
      const oldConnectionState = ctx.getState().realTimeConnectionStatus;

      ctx.patchState({
         realTimeConnectionStatus: action.newConnectionState,
         sessionIdUsedForSignalR: action.sessionIdUsedForSignalR,
         connectionInfo: action.connectionInfo,
      });

      if (oldConnectionState !== RealTimeConnectionStatus.DISCONNECTED && action.newConnectionState === RealTimeConnectionStatus.DISCONNECTED) {
         console.warn(`Connection disconnected, therefore SignalR reconnect is required.`, action);
         ctx.dispatch(new DisconnectSignalR());
         ctx.dispatch(new ReconnectSignalR());
      }
   }

   @Action(ConnectSignalR)
   public ConnectSignalR(ctx: StateContext<ServerCommunicationStateModel>, action: ConnectSignalR) {
      console.log('Start SignalR connection');
      return this.signalRService.startConnection$();
   }

   @Action(DisconnectSignalR)
   public DisconnectSignalRAction(ctx: StateContext<ServerCommunicationStateModel>, action: DisconnectSignalR) {
      console.log('Stop SignalR connection');
      this.signalRService.stopConnection();
   }

   @Action(WebRtcStartClientEndpoint)
   public webRtcStartClientEndpoint(ctx: StateContext<ServerCommunicationStateModel>, action: WebRtcStartClientEndpoint) {
      if (this.store.selectSnapshot(ClientPhoneState.getBrowserTabId) !== action.callInitiatorId) {
         console.log(`Received [WebRtcStartClientEndpoint] with not matching browser tab id '${action.callInitiatorId}', therefore ignoring this message`);
         return;
      }
      return this.webRtcConnectionService.startWebRtcClientEndpoint$(action.iceServers);
   }

   @Action(WebRtcSdpSignalingAdded)
   public WebRtcSdpSignalingAdded(ctx: StateContext<ServerCommunicationStateModel>, action: WebRtcSdpSignalingAdded) {
      return this.webRtcConnectionService.sdpReceivedFromServer$(action.sdpOffer);
   }

   @Action(WebRtcIceCandidatesAddedByServer)
   public WebRtcIceCandidatesAdded(ctx: StateContext<ServerCommunicationStateModel>, action: WebRtcIceCandidatesAddedByServer) {
      return this.webRtcConnectionService.iceCandidatesAdded$(action.iceCandidates);
   }

   @Action(WebRtcIceCandidatesAddedByBrowser)
   public WebRtcIceCandidatesAddedByBrowser(ctx: StateContext<ServerCommunicationStateModel>, action: WebRtcIceCandidatesAddedByBrowser) {
      return this.webRtcService.addWebRtcIceCandidates({
         iceCandidates: action.iceCandidates,
      });
   }

   @Action(WebRtcNegotiationNeeded)
   public WebRtcNegotiationNeeded(ctx: StateContext<ServerCommunicationStateModel>, action: WebRtcNegotiationNeeded) {
      return this.webRtcConnectionService.negotiationNeeded$();
   }

   @Action(WebRtcCloseConnection)
   public WebRtcCloseConnection(ctx: StateContext<ServerCommunicationStateModel>, action: WebRtcCloseConnection) {
      this.webRtcConnectionService.closeConnection();
   }

   @Action(RealTimeConnectionStatusChanged)
   public realTimeConnectionStatusChanged(ctx: StateContext<ServerCommunicationStateModel>, action: RealTimeConnectionStatusChanged) {
      ctx.patchState({
         isRegistrationConfirmedAtServer: action.realTimeConnectionStatusData.isRegistrationConfirmedAtServer,
      });
   }

   @Action(UserSystemMonitorDataChanged)
   public userSystemMonitorDataChanged(ctx: StateContext<ServerCommunicationStateModel>, action: UserSystemMonitorDataChanged) {
      ctx.patchState({
         responsibleHostForUser: action.data.responsibleHostForUser,
         sessionInfos: _.mapValues(action.data.sessionInfos, (value) => {
            return <IUserSessionMonitoringInfo>{
               sessionId: value.sessionId,
               isLongTermSession: value.isLongTermSession,
               sessionType: SessionTypeEnum.tryValueOf(value.sessionType) ?? SessionTypeEnum.UNKNOWN,
               availableRealtimeConnections: value.availableRealTimeConnections,
            };
         }),
         userVersionInfo: action.data.userVersionInfo,
      });
   }

   @Action(SamwinLogonErrorOccurred)
   public samwinLogonErrorOccurred(ctx: StateContext<ServerCommunicationStateModel>, action: SamwinLogonErrorOccurred) {
      return combineLatest([
         this.translateService.get('notification.logon_error.title', { errorType: action.data.errorType }),
         this.translateService.get('notification.logon_error.message', { reason: action.data.errorReason }),
      ]).pipe(switchMap(([title, message]) => ctx.dispatch(new ToastUserInfoNotification(NotificationType.ERROR, title, message, { disableTimeOut: true }))));
   }
}
